// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/fenced-imports */
/* eslint-disable react/no-array-index-key */
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { Typography } from "@insite/mobius/index";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    tips = "tips",
}

export interface SearchResultsTip {
    fields: {
        image: string;
        title: string;
        description: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.tips]: SearchResultsTip[];
    };
}

export interface SearchResultTipsStyles {
    wrapper?: InjectableCss;
    tipWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    image?: LazyImageProps;
    title?: TypographyProps;
    titleWrapper?: TypographyProps;
}

export const searchResultTipsStyles: SearchResultTipsStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            flex-wrap: wrap;
            row-gap: 16px;
        `,
    },
    tipWrapper: {
        css: css`
            flex: 1;
            max-width: 246px;
            margin: 0 auto;
            display: flex;
            flex-direction: column;
            padding: 0 8px;
            &:nth-child(4n + 1) {
                padding: 0 8px 0 0;
            }
        `,
    },
    imageWrapper: {
        css: css`
            width: 250px;
            height: 275px;
        `,
    },
    image: {
        css: css`
            max-width: 100%;
            max-height: 100%;
            height: auto;
        `,
    },
    titleWrapper: {
        css: css`
            margin: 10px 16px;
        `,
    },
    title: {
        variant: "h5",
        as: "p",
        css: css`
            text-align: center;
        `,
    },
};

type Props = OwnProps;

const styles = searchResultTipsStyles;

export const SearchResultTips: FC<Props> = ({ fields }) => {
    return (
        <StyledWrapper {...styles.wrapper}>
            {fields.tips.map((tip, index) => (
                <StyledWrapper key={index} {...styles.tipWrapper}>
                    {tip.fields.image && (
                        <StyledWrapper {...styles.imageWrapper}>
                            <LazyImage src={tip.fields.image} {...styles.image} />
                        </StyledWrapper>
                    )}
                    {tip.fields.title && (
                        <StyledWrapper {...styles.titleWrapper}>
                            <Typography {...styles.title}>{tip.fields.title}</Typography>
                        </StyledWrapper>
                    )}
                    {parse(tip.fields.description, parserOptions)}
                </StyledWrapper>
            ))}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: SearchResultTips,
    definition: {
        group: "Xngage Custom" as any,
        icon: "link",
        fieldDefinitions: [
            {
                name: fields.tips,
                editorTemplate: "ListField",
                defaultValue: [],
                fieldType: "General",
                getDisplay(item) {
                    return item.fields.title;
                },
                fieldDefinitions: [
                    {
                        name: "image",
                        displayName: "Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "description",
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
